import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createMindMap,
  deleteMindMap,
  listMindMaps,
  removeSavedMindMapLink,
  saveMindMapLink,
} from '../../utils/mindmap/apiClient';

const roleLabel = (map) => {
  if (map.accessSource === 'owner') return 'Owner';
  if (map.accessSource === 'saved_link') {
    return map.accessRole === 'editor' ? 'Saved · Edit' : 'Saved · View';
  }
  return map.accessRole === 'editor' ? 'Editor' : 'Viewer';
};

const mapTitle = (map) => map.title?.trim() || 'Untitled map';

const MapsIcon = () => (
  <svg
    aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="4" width="13" height="13" rx="2" />
    <path d="M8 20h10a2 2 0 0 0 2-2V8" />
  </svg>
);

const ImportIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const TrashIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v5" />
    <path d="M14 11v5" />
  </svg>
);

const MapsMenu = ({ currentMap, isDarkMode, mapFileActions, onOpen }) => {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [maps, setMaps] = useState([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingMapId, setDeletingMapId] = useState(null);
  const [error, setError] = useState(null);
  const [savedCurrentMap, setSavedCurrentMap] = useState(false);

  const currentMapId = currentMap?.mindmapId || null;
  const canSaveCurrentMap = currentMapId
    && currentMap?.accessSource === 'link'
    && !savedCurrentMap;
  const canImportCurrentMap = typeof mapFileActions?.importIntoCurrentMap === 'function';

  const palette = {
    panel: isDarkMode ? '#111827' : '#ffffff',
    panelMuted: isDarkMode ? '#1f2937' : '#f3f4f6',
    border: isDarkMode ? '#374151' : '#e5e7eb',
    text: isDarkMode ? '#f9fafb' : '#111827',
    muted: isDarkMode ? '#9ca3af' : '#6b7280',
    accent: isDarkMode ? '#60a5fa' : '#2563eb',
  };

  const loadMaps = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listMindMaps();
      setMaps(Array.isArray(result) ? result : []);
    } catch (loadError) {
      setError(loadError?.message || 'Could not load your maps.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    loadMaps();
    const focusTimer = setTimeout(() => searchRef.current?.focus(), 0);
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, loadMaps]);

  useEffect(() => {
    setSavedCurrentMap(currentMap?.accessSource === 'saved_link');
  }, [currentMapId, currentMap?.accessSource]);

  const filteredMaps = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return maps;
    return maps.filter((map) => mapTitle(map).toLowerCase().includes(normalized));
  }, [maps, query]);

  const openMap = (id) => {
    if (typeof window === 'undefined') return;
    window.location.assign(`/?map=${encodeURIComponent(id)}`);
  };

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const created = await createMindMap(null);
      if (!created?.id) throw new Error('The new map could not be created.');
      openMap(created.id);
    } catch (createError) {
      setError(createError?.message || 'The new map could not be created.');
      setIsCreating(false);
    }
  };

  const handleSaveCurrent = async () => {
    if (!currentMapId) return;
    setIsSaving(true);
    setError(null);
    try {
      await saveMindMapLink(currentMapId);
      setSavedCurrentMap(true);
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
        hashParams.delete('share');
        const nextHash = hashParams.toString();
        window.history.replaceState(
          null,
          '',
          `${url.pathname}${url.search}${nextHash ? `#${nextHash}` : ''}`
        );
      }
      await loadMaps();
    } catch (saveError) {
      setError(saveError?.message || 'This map could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveSaved = async (event, map) => {
    event.stopPropagation();
    try {
      await removeSavedMindMapLink(map.id);
      setMaps((previous) => previous.filter((candidate) => candidate.id !== map.id));
      if (map.id === currentMapId) setSavedCurrentMap(false);
    } catch (removeError) {
      setError(removeError?.message || 'This map could not be removed.');
    }
  };

  const handleDelete = async (event, map) => {
    event.stopPropagation();
    const title = mapTitle(map);
    if (typeof window === 'undefined'
      || !window.confirm(`Delete “${title}”? This can’t be undone.`)) return;

    setDeletingMapId(map.id);
    setError(null);
    try {
      await deleteMindMap(map.id);
      if (map.id === currentMapId) {
        window.location.assign('/');
        return;
      }
      setMaps((previous) => previous.filter((candidate) => candidate.id !== map.id));
    } catch (deleteError) {
      setError(deleteError?.message || 'This map could not be deleted.');
    } finally {
      setDeletingMapId(null);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'flex' }}>
      <button
        type="button"
        aria-label="Maps"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title="Maps"
        onClick={(event) => {
          event.stopPropagation();
          const nextOpen = !isOpen;
          setIsOpen(nextOpen);
          if (nextOpen && onOpen) onOpen();
        }}
        style={{
          width: 28,
          height: 28,
          padding: 0,
          border: 'none',
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDarkMode ? '#111827' : '#e5e7eb',
          color: palette.text,
          cursor: 'pointer',
        }}
      >
        <MapsIcon />
      </button>

      {isOpen && (
        <section
          role="dialog"
          aria-label="My maps"
          style={{
            position: 'fixed',
            top: 52,
            right: 12,
            width: 'min(340px, calc(100vw - 24px))',
            padding: 12,
            borderRadius: 12,
            background: palette.panel,
            border: `1px solid ${palette.border}`,
            boxShadow: '0 18px 40px rgba(0,0,0,0.35)',
            color: palette.text,
            zIndex: 220,
            fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <strong style={{ fontSize: 15 }}>My maps</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
                style={{
                  border: 0,
                  borderRadius: 7,
                  padding: '6px 9px',
                  background: palette.accent,
                  color: '#fff',
                  cursor: isCreating ? 'wait' : 'pointer',
                  fontWeight: 700,
                }}
              >
                {isCreating ? 'Creating…' : '+ New map'}
              </button>
              {canImportCurrentMap && (
                <button
                  type="button"
                  onClick={mapFileActions.importIntoCurrentMap}
                  title="Replace the current map with an imported file"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    padding: '6px 9px',
                    border: `1px solid ${palette.border}`,
                    borderRadius: 7,
                    background: palette.panelMuted,
                    color: palette.text,
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  <ImportIcon />
                  Import map
                </button>
              )}
            </div>
          </div>

          {canSaveCurrentMap && (
            <button
              type="button"
              onClick={handleSaveCurrent}
              disabled={isSaving}
              style={{
                width: '100%',
                marginTop: 10,
                padding: '8px 10px',
                border: `1px solid ${palette.accent}`,
                borderRadius: 8,
                background: 'transparent',
                color: palette.accent,
                cursor: isSaving ? 'wait' : 'pointer',
                textAlign: 'left',
                fontWeight: 700,
              }}
            >
              {isSaving ? 'Saving…' : 'Save current map to My maps'}
            </button>
          )}

          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search maps"
            aria-label="Search maps"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              marginTop: 10,
              padding: '8px 10px',
              borderRadius: 8,
              border: `1px solid ${palette.border}`,
              background: palette.panelMuted,
              color: palette.text,
              outline: 'none',
            }}
          />

          {error && (
            <div role="alert" style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 8, maxHeight: 330, overflowY: 'auto' }}>
            {isLoading && maps.length === 0 ? (
              <div style={{ padding: 14, color: palette.muted, fontSize: 13 }}>Loading maps…</div>
            ) : filteredMaps.length === 0 ? (
              <div style={{ padding: 14, color: palette.muted, fontSize: 13 }}>
                {query ? 'No maps match your search.' : 'No maps yet.'}
              </div>
            ) : filteredMaps.map((map) => {
              const isCurrent = map.id === currentMapId;
              return (
                <div
                  key={map.id}
                  style={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    gap: 8,
                    alignItems: 'center',
                    padding: '9px 8px',
                    border: 0,
                    borderRadius: 8,
                    background: isCurrent ? palette.panelMuted : 'transparent',
                    color: palette.text,
                  }}
                >
                  <button
                    type="button"
                    aria-current={isCurrent ? 'page' : undefined}
                    onClick={() => openMap(map.id)}
                    style={{
                      minWidth: 0,
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 13,
                        fontWeight: isCurrent ? 700 : 600,
                      }}
                    >
                      {mapTitle(map)}
                    </span>
                    <span style={{ display: 'block', marginTop: 2, color: palette.muted, fontSize: 11 }}>
                      {roleLabel(map)}{isCurrent ? ' · Current' : ''}
                    </span>
                  </button>
                  {map.accessSource === 'owner' && (
                    <button
                      type="button"
                      aria-label={`Delete ${mapTitle(map)}`}
                      title="Delete map"
                      disabled={deletingMapId === map.id}
                      onClick={(event) => handleDelete(event, map)}
                      style={{
                        padding: 5,
                        border: 0,
                        background: 'transparent',
                        color: palette.muted,
                        fontSize: 16,
                        cursor: deletingMapId === map.id ? 'wait' : 'pointer',
                      }}
                    >
                      {deletingMapId === map.id ? '…' : <TrashIcon />}
                    </button>
                  )}
                  {map.accessSource === 'saved_link' && !isCurrent && (
                    <button
                      type="button"
                      aria-label={`Remove ${mapTitle(map)} from My maps`}
                      title="Remove from My maps"
                      onClick={(event) => handleRemoveSaved(event, map)}
                      style={{
                        padding: 5,
                        border: 0,
                        background: 'transparent',
                        color: palette.muted,
                        fontSize: 16,
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default MapsMenu;
